import { useEffect, useState, useRef } from 'react';
import toast from 'react-hot-toast';
import supabase from '../../utils/supabaseClient';
import logger from '../../utils/logger';
import { getStatusMessage } from '../../utils/liveSession/sessionUtils';
import { parseMetadataAnswers } from '../../utils/liveSession/answerUtils';

/**
 * Hook for managing Supabase real-time subscriptions
 * @param {string} sessionId - Session ID
 * @param {Object} session - Session data
 * @param {boolean} isConsultant - Whether user is consultant
 * @param {Object} setters - Object containing state setters
 * @param {Function} setters.setSession - Set session state
 * @param {Function} setters.setCurrentQuestionIndex - Set current question index
 * @param {Function} setters.setCurrentAnswers - Set current answers
 * @param {Object} refs - Object containing refs
 * @param {Object} refs.isNavigatingRef - Ref to track navigation state
 * @returns {Object} Realtime channel state
 */
export const useSessionSubscriptions = ({
  sessionId,
  session,
  isConsultant,
  setSession,
  setCurrentQuestionIndex,
  setCurrentAnswers,
  isNavigatingRef,
  fetchSession // Add fetchSession for polling fallback
}) => {
  const [realtimeChannel, setRealtimeChannel] = useState(null);
  const [isRealtimeConnected, setIsRealtimeConnected] = useState(false);
  const isRealtimeConnectedRef = useRef(false); // Ref to track connection status for polling

  // Set up Supabase Realtime subscription (separate effect to avoid recreating channel)
  useEffect(() => {
    // Check if Supabase is properly configured
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL?.trim() || '';
    const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY?.trim() || '';
    
    if (!supabaseUrl || !supabaseKey) {
      logger.warn('⚠️ Supabase not configured. Skipping realtime subscription.');
      logger.warn('Session will work without real-time updates. Configure Supabase for full functionality.');
      return;
    }
    
    logger.log('🔌 Setting up Realtime subscription for session:', sessionId);
    
    let errorToastShown = false; // Track if error toast has been shown
    let connectionAttempts = 0; // Track connection attempts
    
    const channel = supabase
      .channel(`live_session_${sessionId}`, {
        config: {
          broadcast: { self: false }, // Don't receive own broadcasts
          presence: { key: sessionId }
        }
      })
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'live_sessions',
          filter: `id=eq.${sessionId}`
        },
        (payload) => {
          logger.log('🔄 Session Realtime update received:', payload);
          logger.log('   New data:', payload.new);
          logger.log('   Old data:', payload.old);
          
          // Update session with new data
          if (payload.new) {
            setSession(prevSession => ({
              ...prevSession,
              ...payload.new
            }));
            
            // Sync question navigation - USE FUNCTIONAL UPDATE to avoid stale closure
            if (payload.new.current_question_index !== undefined) {
              const newIndex = payload.new.current_question_index;
              
              setCurrentQuestionIndex(prevIndex => {
                if (newIndex !== prevIndex) {
                  logger.log(`📍 Syncing question index: ${prevIndex} → ${newIndex}, isNavigating: ${isNavigatingRef.current}`);
                  
                  // Show toast to consultant when user navigates (not when consultant navigates themselves)
                  // isNavigatingRef tracks if we're actively navigating to prevent showing our own toast
                  if (isConsultant && !isNavigatingRef.current) {
                    toast.success(`Question ${newIndex + 1}`, { 
                      id: 'question-sync', 
                      duration: 1500,
                      position: 'top-center'
                    });
                  }
                  
                  return newIndex;
                }
                return prevIndex;
              });
            }
            
            // Show toast for status changes
            if (payload.new.status !== payload.old?.status) {
              const message = getStatusMessage(payload.new.status);
              if (message) {
                toast.success(message, { 
                  id: `status-${payload.new.status}`, 
                  duration: 4000,
                  position: 'top-center'
                });
              }
            }
          }
        }
      )
      .subscribe((status) => {
        logger.log('📡 Session subscription status:', status);
        if (status === 'SUBSCRIBED') {
          logger.log('✅ Subscribed to live session updates');
          setIsRealtimeConnected(true);
          errorToastShown = false; // Reset error flag on successful connection
          connectionAttempts = 0; // Reset connection attempts
          // Don't show success toast - it's not necessary and can be annoying
          // toast.success('Connected to live session', { 
          //   id: 'session-connected', 
          //   duration: 2000,
          //   position: 'top-center'
          // });
        } else if (status === 'CLOSED') {
          logger.log('❌ Session subscription closed');
          setIsRealtimeConnected(false);
          // Don't show error for normal closure
        } else if (status === 'CHANNEL_ERROR') {
          setIsRealtimeConnected(false);
          connectionAttempts++;
          // Log error but don't spam console
          if (connectionAttempts <= 1) {
            logger.warn('❌ Session subscription error. This may be due to Supabase authentication or configuration.');
            logger.warn('Session will continue to work, but real-time updates may be limited.');
            logger.warn('To fix: Check your REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY environment variables.');
          } else {
            // Only log once to avoid console spam
            logger.debug('Session subscription still in error state (attempt ' + connectionAttempts + ')');
          }
          // Don't show toast - auth errors are configuration issues that don't need user notification
          // The session will still work without real-time updates
        } else if (status === 'TIMED_OUT') {
          logger.warn('⏱️ Session subscription timed out');
          // Don't show error for timeout - it will retry
        } else if (status === 'SUBSCRIBING') {
          logger.log('🔄 Subscribing to live session...');
          // Don't show anything while subscribing
        }
      });
    
    setRealtimeChannel(channel);
    
    // Cleanup on unmount
    return () => {
      logger.log('🔌 Cleaning up Session subscription');
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, [sessionId, setSession, setCurrentQuestionIndex, isConsultant, isNavigatingRef]);
  
  // Set up second realtime subscription for assessment answers (both users see updates)
  // Also subscribe to live_session_answers table for direct answer updates
  useEffect(() => {
    // Check if Supabase is properly configured
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL?.trim() || '';
    const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY?.trim() || '';
    
    if (!supabaseUrl || !supabaseKey) {
      logger.warn('⚠️ Supabase not configured. Skipping answers subscription.');
      return;
    }
    
    // Wait for session to be available
    if (!session) {
      logger.log('⏳ Session not loaded yet, waiting for session data...');
      return;
    }
    
    // Only subscribe if session is active (not completed/cancelled)
    if (session.status === 'completed' || session.status === 'cancelled') {
      logger.log('⏳ Session is completed/cancelled, skipping answers subscription');
      return;
    }
    
    // Need either assessment_id or sessionId to subscribe
    if (!session.assessment_id && !sessionId) {
      logger.log('⏳ No assessment_id or sessionId yet, skipping answers subscription');
      return;
    }
    
    logger.log('🔌 Setting up Realtime subscription for answers:', session.assessment_id || sessionId);
    logger.log('   Session status:', session.status);
    logger.log('   Assessment ID:', session.assessment_id);
    
    let errorToastShown = false; // Track if error toast has been shown
    let connectionAttempts = 0; // Track connection attempts
    
    const answersChannel = supabase
      .channel(`assessment_answers_${session.assessment_id || sessionId}`, {
        config: {
          broadcast: { self: true }, // Allow self to see updates too
          presence: { key: session.assessment_id || sessionId }
        }
      });
    
    // Only subscribe to completed_assessments if assessment_id exists
    // This is the primary source but requires assessment_id to be set
    if (session.assessment_id) {
      answersChannel.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'completed_assessments',
          filter: `id=eq.${session.assessment_id}`
        },
        (payload) => {
          logger.log('📝 Answers Realtime update received (metadata):', payload);
          logger.log('   Metadata updated at:', payload.new?.updated_at);
          
          // Update answers when metadata changes
          if (payload.new && payload.new.metadata) {
            const answersMap = parseMetadataAnswers(payload.new.metadata);
            const answerCount = Object.keys(answersMap).length;
            
            logger.log('   Answers in metadata:', answerCount);
            
            // Always update answers, even if count is 0 (to clear stale data)
            setCurrentAnswers(prevAnswers => {
              const prevCount = Object.keys(prevAnswers).filter(qId => {
                const answer = prevAnswers[qId];
                return answer && (
                  (answer.answer && answer.answer.trim() !== '') ||
                  answer.answer_numeric !== null && answer.answer_numeric !== undefined ||
                  answer.answer_boolean !== null && answer.answer_boolean !== undefined
                );
              }).length;
              const newCount = Object.keys(answersMap).filter(qId => {
                const answer = answersMap[qId];
                return answer && (
                  (answer.answer && answer.answer.trim() !== '') ||
                  answer.answer_numeric !== null && answer.answer_numeric !== undefined ||
                  answer.answer_boolean !== null && answer.answer_boolean !== undefined
                );
              }).length;
              
              logger.log(`📊 Updating answers: ${prevCount} → ${newCount} (total keys: ${Object.keys(answersMap).length})`);
              
              // Merge with existing answers to preserve unsaved local changes
              const mergedAnswers = { ...prevAnswers, ...answersMap };
              
              return mergedAnswers;
            });
          }
        }
      );
    } else {
      logger.log('⚠️ No assessment_id available, skipping completed_assessments subscription');
      logger.log('   Will rely on live_session_answers subscription for real-time updates');
    }
    
    // Always subscribe to live_session_answers table (this works with sessionId)
    // This is the PRIMARY subscription when assessment_id is not available
    logger.log(`🔍 Setting up live_session_answers subscription with filter: session_id=eq.${sessionId}`);
    answersChannel.on(
        'postgres_changes',
        {
          event: '*', // INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'live_session_answers',
          filter: `session_id=eq.${sessionId}`
        },
        (payload) => {
          logger.log('📝 Live session answer update received:', payload);
          logger.log('   Event type:', payload.eventType);
          logger.log('   New data:', payload.new);
          logger.log('   Old data:', payload.old);
          logger.log('   Full payload:', JSON.stringify(payload, null, 2));
          
          if (payload.new && payload.eventType !== 'DELETE') {
            const questionId = payload.new.question_id;
            const answerData = {
              question_id: questionId,
              answer: payload.new.answer,
              answer_numeric: payload.new.answer_numeric,
              answer_boolean: payload.new.answer_boolean,
              answer_json: payload.new.answer_json,
              notes: payload.new.notes
            };
            
            // Update current answers with this specific answer
            // Use functional update to ensure we get latest state
            setCurrentAnswers(prevAnswers => {
              const prevAnswersCopy = { ...prevAnswers };
              prevAnswersCopy[questionId] = answerData;
              
              // Log for debugging (especially for consultant)
              if (isConsultant) {
                logger.log(`👁️ Consultant: Updated answer for question ${questionId}`);
                logger.log(`   Answer:`, answerData);
              } else {
                logger.log(`✅ Updated answer for question ${questionId} from live_session_answers`);
              }
              
              return prevAnswersCopy;
            });
            
            // Show toast to consultant when user answers a question
            if (isConsultant && (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE')) {
              toast.success('Answer received', { 
                id: `answer-${questionId}`, 
                duration: 2000,
                position: 'top-center',
                icon: '✓'
              });
            }
          } else if (payload.eventType === 'DELETE' && payload.old) {
            // Remove answer if deleted
            const questionId = payload.old.question_id;
            setCurrentAnswers(prevAnswers => {
              const updated = { ...prevAnswers };
              delete updated[questionId];
              logger.log(`🗑️ Removed answer for question ${questionId}`);
              return updated;
            });
          }
        }
      )
      .subscribe((status) => {
        logger.log('📡 Answers subscription status:', status);
        if (status === 'SUBSCRIBED') {
          if (session.assessment_id) {
            logger.log(`✅ Subscribed to answer updates for assessment: ${session.assessment_id}`);
          } else {
            logger.log(`✅ Subscribed to answer updates via live_session_answers (session: ${sessionId})`);
          }
          setIsRealtimeConnected(true); // Mark as connected
          isRealtimeConnectedRef.current = true; // Update ref for polling logic
          errorToastShown = false; // Reset error flag on successful connection
          // Don't show success toast - it's not necessary and can be annoying
          // toast.success('Synced with assessment', { 
          //   id: 'answers-synced', 
          //   duration: 1500,
          //   position: 'top-center'
          // });
        } else if (status === 'CHANNEL_ERROR') {
          connectionAttempts++;
          logger.error('❌ Failed to subscribe to answers');
          
          // Only log error, don't show toast - WebSocket errors are often transient
          // The session works fine without real-time answer sync (answers are saved via API)
          if (connectionAttempts <= 2) {
            logger.warn('⚠️ Answers subscription error (attempt ' + connectionAttempts + '). This is usually a transient WebSocket issue.');
            logger.warn('Session will continue to work without real-time answer sync. Answers are saved via API.');
          } else {
            logger.debug('Answers subscription still in error state (attempt ' + connectionAttempts + ')');
          }
          
          // Don't show error toast - it's not critical and can be annoying
          // The session works fine without real-time answer sync (answers are saved via API)
        } else if (status === 'CLOSED') {
          logger.log('❌ Answers subscription closed');
          setIsRealtimeConnected(false); // Mark as disconnected
          isRealtimeConnectedRef.current = false; // Update ref for polling logic
          // Don't show error for normal closure
        } else if (status === 'TIMED_OUT') {
          logger.warn('⏱️ Answers subscription timed out');
          // Don't show error for timeout - it will retry
        } else if (status === 'SUBSCRIBING') {
          logger.log('🔄 Subscribing to answer updates...');
          // Don't show anything while subscribing
        }
      });
    
    // For consultants, set up a fallback polling mechanism
    // This ensures consultants see updates even if real-time subscriptions don't work
    // CRITICAL: When assessment_id is null, real-time might not work, so we need polling
    let pollInterval = null;
    let pollingTimeout = null;
    
    if (isConsultant && fetchSession) {
      // If assessment_id is null, start polling immediately (real-time likely won't work)
      // If assessment_id exists, wait a bit to see if real-time connects
      const shouldPollImmediately = !session?.assessment_id;
      const delay = shouldPollImmediately ? 2000 : 10000; // 2s if no assessment_id, 10s otherwise
      
      logger.log(`🔄 Setting up polling for consultant (delay: ${delay}ms, assessment_id: ${session?.assessment_id || 'null'})`);
      
      pollingTimeout = setTimeout(() => {
        // For immediate polling (no assessment_id), always poll
        // For delayed polling (has assessment_id), only poll if subscription not connected
        const shouldStartPolling = shouldPollImmediately || !isRealtimeConnectedRef.current;
        
        if (shouldStartPolling) {
          if (shouldPollImmediately) {
            logger.log('🔄 Starting immediate polling (no assessment_id - real-time may not work)');
          } else {
            logger.log('⚠️ Subscription not connected after 10s, starting fallback polling');
          }
          
          pollInterval = setInterval(() => {
            // Always poll if no assessment_id, otherwise check connection status
            if (shouldPollImmediately || !isRealtimeConnectedRef.current) {
              logger.log('🔄 Polling for answer updates');
              // Trigger a session refresh to get latest answers (silent refresh)
              fetchSession(false);
            } else {
              logger.log('✅ Subscription connected, stopping fallback polling');
              if (pollInterval) {
                clearInterval(pollInterval);
                pollInterval = null;
              }
            }
          }, shouldPollImmediately ? 2000 : 5000); // Poll every 2s if no assessment_id, 5s otherwise
        }
      }, delay);
    }
    
    // Cleanup on unmount or when key dependencies change
    return () => {
      logger.log('🔌 Cleaning up Answers subscription');
      if (answersChannel) {
        supabase.removeChannel(answersChannel);
      }
      if (pollInterval) {
        clearInterval(pollInterval);
      }
      if (pollingTimeout) {
        clearTimeout(pollingTimeout);
      }
    };
  }, [
    sessionId, // Always stable - session ID doesn't change
    session?.assessment_id, // Only re-subscribe if assessment_id changes
    session?.status, // Only re-subscribe if status changes
    isConsultant, // Only re-subscribe if role changes
    // Don't include session object itself - use specific properties instead
    // Don't include setCurrentAnswers - it's stable
    // Don't include isRealtimeConnected - it changes too often
    // Don't include fetchSession - it's stable
  ]);

  // Polling fallback when realtime is not connected (especially for consultant view)
  useEffect(() => {
    // Only poll if realtime is not connected and we have a session
    if (isRealtimeConnected || !session || !sessionId || !fetchSession) {
      return;
    }
    
    // Poll more frequently for consultant to see updates quickly
    const pollInterval = isConsultant ? 2000 : 5000; // Consultant: 2s, User: 5s
    
    logger.log(`🔄 Starting polling fallback (realtime not connected). Interval: ${pollInterval}ms`);
    
    const pollTimer = setInterval(async () => {
      try {
        // Fetch latest session data - this will update session, question index, and answers
        // Pass false to showLoading to avoid showing loading spinner during polling
        if (typeof fetchSession === 'function') {
          await fetchSession(false);
        }
      } catch (err) {
        logger.error('Error polling session:', err);
      }
    }, pollInterval);
    
    return () => {
      logger.log('🛑 Stopping polling fallback');
      clearInterval(pollTimer);
    };
  }, [isRealtimeConnected, session, sessionId, isConsultant, fetchSession]);

  return { realtimeChannel };
};

